import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import WatchIcon from '@mui/icons-material/Watch';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DiamondIcon from '@mui/icons-material/Diamond';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionCard = motion(Card);

const HomePage = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #951520 0%, #6e0e17 100%)',
          color: 'white',
          py: { xs: 10, md: 15 },
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <MotionTypography variant={{ xs: 'h4', md: 'h2' }} component="h1" gutterBottom fontWeight="bold">
                  {t('home.heroTitle')}
                </MotionTypography>
                <MotionTypography variant={{ xs: 'body1', md: 'h5' }} sx={{ mb: 4, opacity: 0.9 }}>
                  {t('home.heroSubtitle')}
                </MotionTypography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/mercato"
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {t('home.ourSelection')}
                  </Button>
                  <Button
                    component="a"
                    href="https://www.chrono24.it/dealer/watchpassionar/index.htm"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="text"
                    size="large"
                    disableRipple
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    ✓ Trusted dealer Chrono24
                  </Button>
                </Stack>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
              >
                <Box
                  component="img"
                  src="https://i.ibb.co/rTKcHJd/Chat-GPT-Image-7-mag-2026-14-21-20.png"
                  alt="Watch Passion"
                  sx={{ width: '100%', maxWidth: 500, height: 'auto', borderRadius: 3, boxShadow: 8 }}
                />
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sezione Caratteristiche */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
            {t('home.whyTitle')}
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary' }}>
            {t('home.whySubtitle')}
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {[
            { icon: <DiamondIcon fontSize="large" />, key: 'premium' },
            { icon: <VerifiedIcon fontSize="large" />, key: 'authenticity' },
            { icon: <LocalShippingIcon fontSize="large" />, key: 'service' },
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                sx={{
                  height: '100%', display: 'flex', flexDirection: 'column', p: 3,
                  borderRadius: 4, boxShadow: 2,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: 'primary.main' }}>
                  {feature.icon}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold" textAlign="center">
                  {t(`home.features.${feature.key}.title`)}
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ flexGrow: 1 }}>
                  {t(`home.features.${feature.key}.desc`)}
                </Typography>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </Container>

    </Box>
  );
};

export default HomePage;
